import { useEffect, useMemo, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Check, Flame, IndianRupee, PhoneCall, Search, Target, Users, X } from "lucide-react";

const TABS = [
  { id: "sales", label: "Sales Pipeline", icon: Users, testId: "crm-tab-sales" },
  { id: "orders", label: "Orders", icon: IndianRupee, testId: "crm-tab-orders" },
  { id: "pipe", label: "Pipeline", icon: Flame, testId: "crm-tab-pipe" },
  { id: "target", label: "Target", icon: Target, testId: "crm-tab-target" },
];

function istMonth(delta = 0) {
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit" });
  const parts = fmt.formatToParts(new Date());
  const y = Number(parts.find((p) => p.type === "year").value);
  const m = Number(parts.find((p) => p.type === "month").value);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function istToday() {
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" });
  return fmt.format(new Date());
}

function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-IN", { month: "long", year: "numeric", timeZone: "UTC" });
}

function moneyText(n) {
  if (n === null || n === undefined || n === "") return "—";
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

function countText(n) {
  if (n === null || n === undefined || n === "") return "—";
  return String(Number(n));
}

function when(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function sourceLabel(source) {
  if (source === "google_ads" || source === "google") return "Google Ads";
  if (source === "facebook") return "Facebook";
  return "";
}

function toneOf(row) {
  if (row.pipeline_status === "order_close") return "won";
  if (row.pipeline_status === "interested") return "hot";
  return "";
}

function fieldClass(tone) {
  if (tone === "hot") return "bg-white/10 border-white/20 text-white placeholder:text-orange-100/70";
  if (tone === "won") return "bg-white border-emerald-200 text-emerald-950";
  return "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400";
}

function TagBox({ tags, known, tone, onChange }) {
  const [draft, setDraft] = useState("");
  const add = (label) => {
    const next = String(label || "").trim();
    if (!next) return;
    if (!tags.some((t) => t.toLowerCase() === next.toLowerCase())) onChange([...tags, next]);
    setDraft("");
  };
  const unused = known.filter((t) => !tags.some((x) => x.toLowerCase() === t.toLowerCase()));
  return (
    <div className="min-w-[180px]">
      <div className="flex flex-wrap gap-1 mb-1">
        {tags.map((t) => (
          <button type="button" key={t} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${tone === "hot" ? "bg-white/15" : "bg-indigo-50 text-indigo-800"}`} onClick={() => onChange(tags.filter((x) => x !== t))}>
            {t} <X className="w-3 h-3" />
          </button>
        ))}
      </div>
      <Input
        value={draft}
        placeholder="Add a tag"
        className={`h-8 text-xs ${fieldClass(tone)}`}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(draft); } }}
        onBlur={() => add(draft)}
      />
      {unused.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1 max-h-16 overflow-auto">
          {unused.slice(0, 8).map((t) => (
            <button type="button" key={t} className={`text-[10px] rounded-full px-2 py-0.5 ${tone === "hot" ? "bg-black/20" : "bg-slate-100 text-slate-600"}`} onClick={() => add(t)}>{t}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusSelect({ row, statuses, onPick }) {
  const tone = toneOf(row);
  return (
    <select
      className={`h-8 rounded-md border px-2 text-xs ${fieldClass(tone)}`}
      value={row.pipeline_status || ""}
      data-testid={`pipeline-status-${row.id}`}
      onChange={(e) => onPick(row, e.target.value)}
    >
      <option value="">Choose status</option>
      {statuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
    </select>
  );
}

export default function SalesCrm() {
  const [tab, setTab] = useState("sales");
  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [tags, setTags] = useState([]);
  const [q, setQ] = useState("");
  const [closeFor, setCloseFor] = useState(null);
  const [paid, setPaid] = useState("");
  const [pending, setPending] = useState("");
  const [month, setMonth] = useState(() => istMonth());
  const [sheet, setSheet] = useState(null);
  const [savingId, setSavingId] = useState("");

  const loadDesk = () => {
    const path = tab === "orders" ? "/sales/pipeline/orders" : tab === "pipe" ? "/sales/pipeline/interested" : "/sales/pipeline/leads";
    const params = tab === "sales" && q.trim() ? { q: q.trim() } : undefined;
    api.get(path, { params }).then(({ data }) => {
      setRows(data.items || []);
      if (data.statuses) setStatuses(data.statuses);
    }).catch((e) => toast.error(extractErr(e)));
  };

  useEffect(() => {
    api.get("/sales/pipeline/tags").then(({ data }) => setTags(data.items || [])).catch(() => {});
    api.get("/sales/pipeline/leads").then(({ data }) => { if (data.statuses) setStatuses(data.statuses); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "target") return undefined;
    loadDesk();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadSheet = (m) => {
    api.get("/sales/pipeline/targets", { params: { month: m } })
      .then(({ data }) => setSheet(data))
      .catch((e) => toast.error(extractErr(e)));
  };

  useEffect(() => {
    if (tab === "target") loadSheet(month);
  }, [tab, month]);

  const replace = (lead) => setRows((list) => list.map((r) => (r.id === lead.id ? lead : r)).filter((r) => {
    if (tab === "orders") return r.pipeline_status === "order_close";
    if (tab === "pipe") return r.pipeline_status === "interested";
    return true;
  }));

  const save = async (id, patch) => {
    const { data } = await api.patch(`/sales/pipeline/leads/${id}`, patch);
    replace(data);
    if (patch.requirement_tags) {
      setTags((prev) => Array.from(new Set([...prev, ...patch.requirement_tags])));
    }
    return data;
  };

  const onPick = (row, value) => {
    if (value === "order_close") {
      setCloseFor(row);
      setPaid(row.amount_paid ?? "");
      setPending(row.pending_payment ?? "");
      return;
    }
    save(row.id, { pipeline_status: value }).catch((e) => toast.error(extractErr(e)));
  };

  const confirmClose = async () => {
    if (!closeFor) return;
    try {
      await save(closeFor.id, { pipeline_status: "order_close", amount_paid: Number(paid), pending_payment: Number(pending) });
      toast.success("Order closed");
      setCloseFor(null);
    } catch (e) { toast.error(extractErr(e)); }
  };

  const bump = async (row) => {
    try {
      const { data } = await api.post(`/sales/pipeline/leads/${row.id}/followup`);
      replace(data);
    } catch (e) { toast.error(extractErr(e)); }
  };

  const blurSave = (row, key, value) => {
    if (String(row[key] ?? "") === String(value ?? "")) return;
    save(row.id, { [key]: value }).catch((e) => toast.error(extractErr(e)));
  };

  const counts = useMemo(() => ({ sales: rows.length }), [rows]);

  const saveArchive = async (person, patch) => {
    setSavingId(person.sales_user_id);
    try {
      const { data } = await api.put(`/sales/pipeline/targets/${person.sales_user_id}`, { month, ...patch });
      setSheet(data);
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSavingId(""); }
  };

  return (
    <div className="space-y-5" data-testid="crm-table">
      <div className="rounded-3xl bg-gradient-to-br from-[#1A237E] via-indigo-800 to-orange-500 text-white p-6 shadow-lg">
        <div className="text-xs uppercase tracking-[0.2em] text-indigo-100">Sales desk</div>
        <h1 className="font-display text-3xl font-bold mt-1">Clients & CRM</h1>
        <p className="text-indigo-100 text-sm mt-2 max-w-2xl">Move a client from the first call to a closed order. Interested rows glow orange. Closed orders turn green and land in Orders.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={t.testId}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition ${on ? "bg-[#1A237E] text-white border-[#1A237E] shadow" : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              {t.id === "sales" && tab === "sales" ? <span className="text-[11px] opacity-80">{counts.sales}</span> : null}
            </button>
          );
        })}
      </div>

      {tab === "sales" && (
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 bg-white text-slate-900 placeholder:text-slate-400" placeholder="Search name, mobile, email, company" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") loadDesk(); }} data-testid="crm-search" />
        </div>
      )}

      {tab !== "target" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-sm">
          <table className="min-w-[1100px] w-full text-sm text-slate-900">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                {tab === "sales" && <th className="px-3 py-3">Date</th>}
                {tab === "orders" && <th className="px-3 py-3">Closed on</th>}
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Mobile</th>
                {tab !== "orders" && <th className="px-3 py-3">Email</th>}
                <th className="px-3 py-3">Company</th>
                {tab !== "orders" && <th className="px-3 py-3">Amount</th>}
                {tab === "orders" && <th className="px-3 py-3">Amount paid</th>}
                {tab === "orders" && <th className="px-3 py-3">Pending</th>}
                {tab !== "orders" && <th className="px-3 py-3">Status</th>}
                {tab === "sales" && <th className="px-3 py-3">Latest notes</th>}
                {tab !== "orders" && <th className="px-3 py-3">Requirement</th>}
                {(tab === "sales" || tab === "pipe") && <th className="px-3 py-3">Next follow-up</th>}
                {tab === "sales" && <th className="px-3 py-3">Follow-ups</th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">No clients on this board yet.</td></tr>
              )}
              {rows.map((row) => {
                const tone = toneOf(row);
                const rowClass = tone === "won" ? "bg-emerald-100 text-emerald-950" : tone === "hot" ? "bg-orange-800 text-orange-50" : "border-t border-slate-100 text-slate-900";
                return (
                  <tr key={row.id} className={rowClass} data-testid={`crm-row-${row.id}`}>
                    {tab === "sales" && <td className="px-3 py-3 whitespace-nowrap">{when(row.created_at)}</td>}
                    {tab === "orders" && <td className="px-3 py-3 whitespace-nowrap">{row.order_closed_on || "—"}</td>}
                    <td className="px-3 py-3">
                      {tab === "orders" ? (
                        <Input defaultValue={row.name} className={`h-8 text-xs ${fieldClass(tone)}`} onBlur={(e) => blurSave(row, "name", e.target.value)} />
                      ) : (
                        <div className="font-semibold">{row.name || "—"} {sourceLabel(row.source) ? <span className="ml-1 text-[10px] font-bold uppercase tracking-wide opacity-80">{sourceLabel(row.source)}</span> : null} {tone === "hot" && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-orange-300 text-orange-950 px-2 py-0.5 text-[10px] font-bold"><Flame className="w-3 h-3" /> Hot lead</span>}</div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {tab === "orders" ? (
                        <Input defaultValue={row.phone} className={`h-8 text-xs w-32 ${fieldClass(tone)}`} onBlur={(e) => blurSave(row, "phone", e.target.value)} />
                      ) : row.phone || "—"}
                    </td>
                    {tab !== "orders" && <td className="px-3 py-3">{row.email || "—"}</td>}
                    <td className="px-3 py-3">
                      {tab === "orders" || tab === "sales" ? (
                        <Input key={`${row.id}-co`} defaultValue={row.company_name} className={`h-8 text-xs w-36 ${fieldClass(tone)}`} onBlur={(e) => blurSave(row, "company_name", e.target.value)} />
                      ) : row.company_name || "—"}
                    </td>
                    {tab !== "orders" && (
                      <td className="px-3 py-3">
                        <Input type="number" min="0" defaultValue={row.amount ?? ""} className={`h-8 text-xs w-24 ${fieldClass(tone)}`} onBlur={(e) => blurSave(row, "amount", e.target.value === "" ? null : Number(e.target.value))} />
                      </td>
                    )}
                    {tab === "orders" && (
                      <td className="px-3 py-3">
                        <Input type="number" min="0" defaultValue={row.amount_paid ?? ""} className={`h-8 text-xs w-24 ${fieldClass(tone)}`} onBlur={(e) => blurSave(row, "amount_paid", Number(e.target.value || 0))} />
                      </td>
                    )}
                    {tab === "orders" && (
                      <td className="px-3 py-3">
                        <Input type="number" min="0" defaultValue={row.pending_payment ?? ""} className={`h-8 text-xs w-24 ${fieldClass(tone)}`} onBlur={(e) => blurSave(row, "pending_payment", Number(e.target.value || 0))} />
                      </td>
                    )}
                    {(tab === "sales" || tab === "pipe") && (
                      <td className="px-3 py-3"><StatusSelect row={row} statuses={statuses} onPick={onPick} /></td>
                    )}
                    {tab === "sales" && (
                      <td className="px-3 py-3">
                        <Input defaultValue={row.latest_notes} placeholder="Notes" className={`h-8 text-xs w-44 ${fieldClass(tone)}`} onBlur={(e) => blurSave(row, "latest_notes", e.target.value)} />
                      </td>
                    )}
                    {tab !== "orders" && (
                      <td className="px-3 py-3">
                        <TagBox tags={row.requirement_tags || []} known={tags} tone={tone} onChange={(next) => save(row.id, { requirement_tags: next }).catch((e) => toast.error(extractErr(e)))} />
                      </td>
                    )}
                    {(tab === "sales" || tab === "pipe") && (
                      <td className="px-3 py-3">
                        <Input type="date" min={tab === "pipe" ? istToday() : undefined} defaultValue={row.next_followup_on || ""} className={`h-8 text-xs ${fieldClass(tone)}`} onBlur={(e) => blurSave(row, "next_followup_on", e.target.value)} />
                      </td>
                    )}
                    {tab === "sales" && (
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-xs font-semibold mb-1">{row.followup_count || 0} call{(row.followup_count || 0) === 1 ? "" : "s"} done</div>
                        <Button type="button" size="sm" variant={tone === "hot" ? "secondary" : "outline"} className={`h-7 text-xs ${tone === "hot" ? "" : "bg-white text-slate-900"}`} data-testid={`followup-done-${row.id}`} onClick={() => bump(row)}>
                          <PhoneCall className="w-3 h-3 mr-1" /> Followup done
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "target" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" onClick={() => setMonth((m) => shiftMonth(m, -1))} className="bg-white text-slate-900">Previous</Button>
            {[0, -1, -2, -3, -4, -5].map((n) => {
              const id = istMonth(n);
              return (
                <button key={id} type="button" onClick={() => setMonth(id)} className={`rounded-full px-3 py-1 text-xs font-semibold border ${month === id ? "bg-[#1A237E] text-white border-[#1A237E]" : "bg-white text-slate-600"}`}>
                  {monthLabel(id)}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-300">Each month starts at zero. Archive follows orders closed in {monthLabel(month)}. Type a number to override it. Miss and over update on their own. Targets are set by super admin.</p>
          <div className="overflow-x-auto rounded-2xl border bg-white text-slate-900">
            <table className="min-w-[980px] w-full text-sm text-slate-900">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  {["Sales person", "Target no.", "Archive no.", "Miss no.", "Over target no.", "Total target amount", "Archive amount", "Miss amount", "Over target amount"].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {(sheet?.items || []).map((p) => (
                  <tr key={p.sales_user_id} className="border-t">
                    <td className="px-3 py-3 font-semibold text-slate-900">{p.name}</td>
                    <td className="px-3 py-3">{p.has_target ? countText(p.target_count) : "Not set"}</td>
                    <td className="px-3 py-3">
                      <Input
                        key={`${p.sales_user_id}-c-${p.archive_count}`}
                        type="number"
                        min="0"
                        defaultValue={p.archive_count}
                        className="h-8 w-24 text-xs bg-white text-slate-900"
                        disabled={savingId === p.sales_user_id}
                        onBlur={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isNaN(n)) return;
                          if (n === Number(p.archive_count_auto)) saveArchive(p, { clear_count: true });
                          else if (n !== Number(p.archive_count)) saveArchive(p, { archive_count: n });
                        }}
                      />
                      <div className="text-[10px] text-slate-400 mt-1">Orders: {p.archive_count_auto}</div>
                    </td>
                    <td className="px-3 py-3 text-amber-700 font-semibold">{countText(p.miss_count)}</td>
                    <td className="px-3 py-3 text-emerald-700 font-semibold">{countText(p.over_count)}</td>
                    <td className="px-3 py-3">{p.has_target ? moneyText(p.target_amount) : "Not set"}</td>
                    <td className="px-3 py-3">
                      <Input
                        key={`${p.sales_user_id}-a-${p.archive_amount}`}
                        type="number"
                        min="0"
                        defaultValue={p.archive_amount}
                        className="h-8 w-28 text-xs bg-white text-slate-900"
                        disabled={savingId === p.sales_user_id}
                        onBlur={(e) => {
                          const n = Number(e.target.value);
                          if (Number.isNaN(n)) return;
                          if (n === Number(p.archive_amount_auto)) saveArchive(p, { clear_amount: true });
                          else if (n !== Number(p.archive_amount)) saveArchive(p, { archive_amount: n });
                        }}
                      />
                    </td>
                    <td className="px-3 py-3 text-amber-700 font-semibold">{moneyText(p.miss_amount)}</td>
                    <td className="px-3 py-3 text-emerald-700 font-semibold">{moneyText(p.over_amount)}</td>
                  </tr>
                ))}
                {(sheet?.items || []).length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No sales people yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {closeFor && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white text-slate-900 rounded-3xl p-6 w-full max-w-md shadow-2xl" data-testid="order-close-dialog">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold"><Check className="w-5 h-5" /> Close this order</div>
            <p className="text-sm text-slate-500 mt-1">{closeFor.name || "Client"} moves to Orders and the row turns green.</p>
            <label className="block text-xs font-semibold text-slate-500 mt-4">Amount paid</label>
            <Input type="number" min="0" value={paid} onChange={(e) => setPaid(e.target.value)} className="mt-1 bg-white text-slate-900" data-testid="order-close-paid" />
            <label className="block text-xs font-semibold text-slate-500 mt-3">Pending payment</label>
            <Input type="number" min="0" value={pending} onChange={(e) => setPending(e.target.value)} className="mt-1 bg-white text-slate-900" data-testid="order-close-pending" />
            <div className="flex justify-end gap-2 mt-5">
              <Button type="button" variant="outline" className="bg-white text-slate-900" onClick={() => setCloseFor(null)}>Cancel</Button>
              <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmClose} data-testid="order-close-save">Save order</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
